// Soft costs — each item can be fixed $ (in project currency) or a % of
// purchase / sale / renovation total. The default VALUES are currency-aware
// because:
//   1. Fixed values like "Architect — R8 000" make zero sense in ZAR (a SA
//      basic residential plan is R25-60k). Old defaults were USD baselines
//      applied verbatim to ZAR projects.
//   2. Percentages like Transfer Costs and Agent Commission are *also*
//      region-specific (SA transfer duty + conveyancing on a R1.5m property
//      ≈ 5-6%; agent commission in SA is 6-7% + VAT vs ~3-3.5% in the US).
//
// We resolve defaults at seed time using the project's currency and fall
// back to USD baselines for currencies we don't yet have explicit data for.

export const MODE_OPTIONS = [
  { value: "fixed",              label: "Fixed $" },
  { value: "per_sqm",            label: "$ per sqm" },
  { value: "per_room",           label: "$ per room" },
  { value: "annual_prorated",    label: "Annual $ × months/12" },
  { value: "percent_purchase",   label: "% of Purchase" },
  { value: "percent_bond",       label: "% of Bond" },
  { value: "bond_interest_auto", label: "Bond Interest (auto)" },
  { value: "percent_sale",       label: "% of Sale" },
  { value: "percent_renovation", label: "% of Reno" },
];

// Default annual bond interest rate by currency. Used as a placeholder
// hint in the Key Inputs UI and as a fallback when bond_rate isn't set
// on the project but a soft-cost line is in `bond_interest_auto` mode.
const DEFAULT_BOND_RATE = {
  USD: 7.5,
  CAD: 6.5,
  EUR: 5.0,
  GBP: 5.5,
  AUD: 6.5,
  NZD: 7.0,
  ZAR: 11.75, // SA prime as of early 2026
};

export function defaultBondRateForCurrency(currency) {
  return DEFAULT_BOND_RATE[currency] || 7.5;
}

// USD baselines — used directly for USD/CAD/EUR/GBP/AUD/NZD-ish projects
// (we pick the closest match below). Sources: residential rule-of-thumb
// budgets for a ~$300k–$650k single-family flip.
const SOFT_COST_DEFAULTS_USD = [
  { key: "architect",      label: "Architect / Plans",        mode: "per_sqm",            value: 25 },
  { key: "engineering",    label: "Engineering",              mode: "per_sqm",            value: 12 },
  { key: "compliance",     label: "Compliance",               mode: "fixed",              value: 2500 },
  { key: "waste",          label: "Skip Hire / Waste Removal", mode: "per_room",          value: 400 },
  { key: "labour_mgmt",    label: "Labour Management",        mode: "percent_renovation", value: 7 },
  { key: "security",       label: "Site Security",            mode: "annual_prorated",    value: 4500 },
  { key: "holding",        label: "Holding Costs (bond)",     mode: "bond_interest_auto", value: 0 },
  { key: "rates_taxes",    label: "Rates & Taxes (annual)",   mode: "annual_prorated",    value: 8000 },
  { key: "transfer",       label: "Transfer Costs",           mode: "percent_purchase",   value: 2 },
  { key: "agent",          label: "Agent Commission",         mode: "percent_sale",       value: 4.5 },
  { key: "contingency",    label: "Contingency",              mode: "percent_renovation", value: 10 },
];

// South African reality (R1.5m–R3m flip in Cape Town / JHB):
//   - Architect: residential alteration plan ~R25-50k, full design ~R50-90k.
//     R35k is a fair conservative midpoint.
//   - Engineering: structural eng ~R10-25k for typical reno → R12k default.
//   - Compliance: building inspector, gas/electric COC → R5k.
//   - Waste: 4-8 skips over a typical reno @ R2-3k each → R12k.
//   - Labour management: 5-8% — slightly lower than US because trade rates
//     are lower so absolute mgmt fees compress; 7% kept as conservative.
//   - Security: site cabin/security service R3-15k depending on duration
//     → R8k default for a 3-4 month flip.
//   - Holding costs: prime ~11% + rates/insurance ÷ 12 × holding months,
//     so a 4-month hold on a R1.5m bonded property is ≈ 3.5-4% of purchase.
//   - Rates & taxes (the standalone item): municipal rates + levies for
//     the holding period → R10k typical.
//   - Transfer costs: transfer duty + conveyancing + bond reg. R1.5m
//     property → ~R45-65k → 4-5% (varies by purchase band, defaults to
//     5% which covers most R1m–R2.5m residential deals).
//   - Agent commission: SA estate agents 5-7.5% + VAT → 6% all-in default
//     (6 × 1.15 ≈ 6.9% net cost to seller, comfortable midpoint).
const SOFT_COST_DEFAULTS_ZAR = [
  { key: "architect",      label: "Architect / Plans",        mode: "per_sqm",            value: 290 },
  { key: "engineering",    label: "Engineering",              mode: "per_sqm",            value: 100 },
  { key: "compliance",     label: "Compliance (COCs etc)",    mode: "fixed",              value: 5000 },
  { key: "waste",          label: "Skip Hire / Waste Removal", mode: "per_room",          value: 3000 },
  { key: "labour_mgmt",    label: "Labour Management",        mode: "percent_renovation", value: 7 },
  { key: "security",       label: "Site Security",            mode: "annual_prorated",    value: 24000 },
  { key: "holding",        label: "Holding Costs (bond int.)", mode: "bond_interest_auto", value: 0 },
  { key: "rates_taxes",    label: "Rates & Taxes (annual)",   mode: "annual_prorated",    value: 25000 },
  { key: "transfer",       label: "Transfer & Bond Costs",    mode: "percent_purchase",   value: 5 },
  { key: "agent",          label: "Agent Commission (+VAT)",  mode: "percent_sale",       value: 6 },
  { key: "contingency",    label: "Contingency",              mode: "percent_renovation", value: 10 },
];

// UK / EUR reality (~£250-450k flip): much higher planning fees, stamp
// duty taken separately, agent commission tighter.
const SOFT_COST_DEFAULTS_GBP = [
  { key: "architect",      label: "Architect / Plans",        mode: "per_sqm",            value: 45 },
  { key: "engineering",    label: "Engineering",              mode: "per_sqm",            value: 18 },
  { key: "compliance",     label: "Compliance / Building Reg", mode: "fixed",             value: 1500 },
  { key: "waste",          label: "Skip Hire / Waste Removal", mode: "per_room",          value: 350 },
  { key: "labour_mgmt",    label: "Labour Management",        mode: "percent_renovation", value: 7 },
  { key: "security",       label: "Site Security",            mode: "annual_prorated",    value: 2400 },
  { key: "holding",        label: "Holding Costs",            mode: "bond_interest_auto", value: 0 },
  { key: "rates_taxes",    label: "Council Tax (annual)",     mode: "annual_prorated",    value: 2400 },
  { key: "transfer",       label: "Stamp Duty + Legal",       mode: "percent_purchase",   value: 4 },
  { key: "agent",          label: "Estate Agent Commission",  mode: "percent_sale",       value: 1.5 },
  { key: "contingency",    label: "Contingency",              mode: "percent_renovation", value: 10 },
];

// Map a currency → its closest defaults table. New currencies fall back
// to USD until we ship explicit data for them.
const DEFAULTS_BY_CURRENCY = {
  USD: SOFT_COST_DEFAULTS_USD,
  CAD: SOFT_COST_DEFAULTS_USD,
  EUR: SOFT_COST_DEFAULTS_GBP,
  GBP: SOFT_COST_DEFAULTS_GBP,
  AUD: SOFT_COST_DEFAULTS_USD,
  NZD: SOFT_COST_DEFAULTS_USD,
  ZAR: SOFT_COST_DEFAULTS_ZAR,
};

// Backwards-compat export — many callers import this directly. Keeps the
// old behaviour for anything that didn't pass a currency.
export const SOFT_COST_DEFAULTS = SOFT_COST_DEFAULTS_USD;

export function defaultsForCurrency(currency) {
  return DEFAULTS_BY_CURRENCY[currency] || SOFT_COST_DEFAULTS_USD;
}

function cryptoRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "sc-" + Math.random().toString(36).slice(2);
}

export function ensureSoftCosts(existing = [], currency = "USD") {
  const defaults = defaultsForCurrency(currency);
  const byKey = new Map(existing.map((i) => [i.key, i]));
  return defaults.map((d) => {
    const prev = byKey.get(d.key);
    if (prev) return prev;
    return {
      id: cryptoRandomId(),
      key: d.key,
      label: d.label,
      enabled: true,
      mode: d.mode,
      value: d.value,
    };
  });
}

export function softCostItemTotal(item, project, renovationCost) {
  if (!item || !item.enabled) return 0;
  const v = Number(item.value) || 0;
  const pp = Number(project?.purchase_price) || 0;
  const sp = Number(project?.target_sale_price) || 0;
  const rc = Number(renovationCost) || 0;
  // Bond / mortgage amount used by "% of Bond" lines (e.g. bond interest
  // holding cost, bond registration). If the user didn't set bond_amount
  // we *fall back to purchase_price* so projects created before the bond
  // field existed don't suddenly drop their holding costs to zero on
  // open. Setting bond_amount=0 explicitly via the input gives the
  // cash-buyer / fully-owned scenario (holding = 0).
  const bond = project?.bond_amount === undefined || project?.bond_amount === null
    ? pp
    : (Number(project.bond_amount) || 0);
  switch (item.mode) {
    case "fixed": return v;
    case "per_sqm": {
      // Drives Architect / Engineering style fees that scale with floor area.
      const sqm = Number(project?.floor_area_sqm) || 0;
      return v * sqm;
    }
    case "per_room": {
      // Drives Skip Hire / Waste / per-room fees. Counts active rooms +
      // extensions so a 4-room flip with a 1-room extension reads as 5.
      const rooms = (project?.rooms || []).length;
      const exts = (project?.extensions || []).length;
      return v * (rooms + exts);
    }
    case "annual_prorated": {
      // Annual fixed amount (e.g. rates & taxes, insurance) pro-rated
      // by the project's hold/reno timeframe. Falls back to 4 months
      // when timeline isn't set so the line still produces a number.
      const months = Number(project?.timeline_months) > 0
        ? Number(project.timeline_months)
        : 4;
      return v * months / 12;
    }
    case "percent_purchase": return pp * v / 100;
    case "percent_bond": return bond * v / 100;
    case "percent_sale": return sp * v / 100;
    case "percent_renovation": return rc * v / 100;
    case "bond_interest_auto": {
      // Auto-calculated from Key Inputs:
      //   bond × annual_rate% × months / 12
      // Falls back to the currency's default annual rate when bond_rate
      // hasn't been set yet, so the line still produces a sensible number
      // out of the box. Months falls back to 4 (typical short flip) when
      // the user hasn't entered a timeline yet.
      const rate = Number(project?.bond_rate) > 0
        ? Number(project.bond_rate)
        : defaultBondRateForCurrency(project?.currency || "USD");
      const months = Number(project?.timeline_months) > 0
        ? Number(project.timeline_months)
        : 4;
      return bond * rate / 100 * months / 12;
    }
    default: return 0;
  }
}

export function softCostsTotal(items = [], project, renovationCost) {
  return items.reduce((sum, i) => sum + softCostItemTotal(i, project, renovationCost), 0);
}
